import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { CATEGORIES } from '../utils/technologies';

const STEPS = ['Ticket', 'Project Info', 'Technologies', 'Review'];

export default function NewReportPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Step 0 — Ticket
  const [ticketQuery, setTicketQuery] = useState('');
  const [ticketResults, setTicketResults] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);

  // Step 1 — Project info
  const [projectTitle, setProjectTitle] = useState('');
  const [clientName, setClientName] = useState('');
  const [description, setDescription] = useState('');
  const [objective, setObjective] = useState('');

  // Step 2 — Technologies
  const [selectedTechs, setSelectedTechs] = useState([]);

  const [saving, setSaving] = useState(false);

  async function searchTickets() {
    if (!ticketQuery.trim()) return;
    setTicketLoading(true);
    try {
      const { data } = await api.get(`/tickets/search?q=${encodeURIComponent(ticketQuery)}`);
      setTicketResults(data);
    } catch {
      setTicketResults([]);
    } finally {
      setTicketLoading(false);
    }
  }

 async function selectTicket(t) {
   try {
     const { data } = await api.get(`/tickets/${t.id}`);
     setSelectedTicket(data);
     if (!projectTitle) setProjectTitle(data.subject || '');
     if (!clientName) setClientName(data.client_name || data.requester || '');
   } catch {
     setSelectedTicket(t);
     if (!projectTitle) setProjectTitle(t.subject || '');
     if (!clientName) setClientName(t.client_name || t.requester || '');
   }
 }

  function toggleTech(tech) {
    setSelectedTechs(prev =>
      prev.find(t => t.technology === tech.technology)
        ? prev.filter(t => t.technology !== tech.technology)
        : [...prev, { category: tech.category, technology: tech.technology, order_index: prev.length }]
    );
  }

  async function createReport() {
    setSaving(true);
    try {
      const { data } = await api.post('/reports/', {
        me_ticket_id: selectedTicket?.id?.toString(),
        udf_fields: selectedTicket?.udf_fields || null,
        me_ticket_ref: selectedTicket?.id,
        project_title: projectTitle,
        client_name: clientName,
        description,
        objective,
        technologies: selectedTechs,
      });
      navigate(`/reports/${data.id}`);
    } catch (err) {
      alert('Error creating report: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('/')}>← Back</button>
        <span style={styles.headerTitle}>New Report</span>
      </header>

      {/* Stepper */}
      <div style={styles.stepper}>
        {STEPS.map((s, i) => (
          <div key={s} style={styles.stepItem}>
            <div style={{ ...styles.stepDot, background: i <= step ? '#156082' : '#E8E8E8' }}>
              {i < step ? '✓' : i + 1}
            </div>
            <span style={{ ...styles.stepLabel, color: i <= step ? '#156082' : '#aaa' }}>{s}</span>
            {i < STEPS.length - 1 && <div style={{ ...styles.stepLine, background: i < step ? '#156082' : '#E8E8E8' }} />}
          </div>
        ))}
      </div>

      <main style={styles.main}>
        {/* Step 0 — Ticket */}
        {step === 0 && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>Link a ManageEngine Ticket</h2>
            <p style={styles.stepHint}>Enter the ME ticket number for direct lookup, or type a subject keyword. You can skip and fill details manually.</p>
            <div style={styles.searchRow}>
              <input
                style={styles.input}
                value={ticketQuery}
                onChange={e => setTicketQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchTickets()}
                placeholder="Ticket number (e.g. 224134) or subject keyword"
              />
              <button style={styles.searchBtn} onClick={searchTickets} disabled={ticketLoading}>
                {ticketLoading ? '…' : 'Search'}
              </button>
            </div>

            {ticketResults.length > 0 && (
              <div style={styles.ticketList}>
                {ticketResults.map(t => (
                  <div
                    key={t.id}
                    style={{ ...styles.ticketRow, background: selectedTicket?.id === t.id ? '#e8f3f8' : '#fff' }}
                    onClick={() => selectTicket(t)}
                  >
                    <div style={styles.ticketId}>#{t.display_id || t.id}</div>
                    <div style={styles.ticketSubject}>{t.subject}</div>
                    <div style={styles.ticketMeta}>{t.requester} · {t.status}</div>
                  </div>
                ))}
              </div>
            )}

            {selectedTicket && (
              <div style={styles.selectedTicket}>
                ✓ Selected: <strong>#{selectedTicket.id}</strong> — {selectedTicket.subject}
              </div>
            )}

            <div style={styles.navRow}>
              <button style={styles.skipBtn} onClick={() => setStep(1)}>Skip →</button>
              <button style={styles.nextBtn} onClick={() => setStep(1)}>
                {selectedTicket ? 'Continue →' : 'Skip →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 1 — Project info */}
        {step === 1 && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>Project Information</h2>
            <label style={styles.label}>Project Title *</label>
            <input style={styles.input} value={projectTitle} onChange={e => setProjectTitle(e.target.value)} placeholder="e.g. VMware Infrastructure Upgrade — Client X" />

            <label style={styles.label}>Client Name</label>
            <input style={styles.input} value={clientName} onChange={e => setClientName(e.target.value)} placeholder="e.g. Empresa ABC" />

            <label style={styles.label}>Project Description</label>
            <textarea style={styles.textarea} value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe what was implemented…" rows={4} />

            <label style={styles.label}>Objective</label>
            <textarea style={styles.textarea} value={objective} onChange={e => setObjective(e.target.value)} placeholder="What was the goal of this project…" rows={3} />

            <div style={styles.navRow}>
              <button style={styles.backStepBtn} onClick={() => setStep(0)}>← Back</button>
              <button style={styles.nextBtn} onClick={() => setStep(2)} disabled={!projectTitle.trim()}>Continue →</button>
            </div>
          </div>
        )}

        {/* Step 2 — Technologies */}
        {step === 2 && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>Select Technologies</h2>
            <p style={styles.stepHint}>Select all technologies you implemented in this project.</p>

            {CATEGORIES.map(cat => (
              <div key={cat.key} style={styles.catBlock}>
                <h3 style={styles.catLabel}>{cat.label}</h3>
                <div style={styles.techGrid}>
                  {cat.technologies.map(tech => {
                    const selected = !!selectedTechs.find(t => t.technology === tech.technology);
                    return (
                      <div
                        key={tech.technology}
                        style={{ ...styles.techCard, ...(selected ? styles.techCardSelected : {}) }}
                        onClick={() => toggleTech(tech)}
                      >
                        <div style={styles.techCardHeader}>
                          <span style={styles.techName}>{tech.label}</span>
                          {tech.status === 'ready'
                            ? <span style={styles.readyBadge}>● Available</span>
                            : <span style={styles.soonBadge}>○ Not Yet Implemented</span>
                          }
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div style={styles.navRow}>
              <button style={styles.backStepBtn} onClick={() => setStep(1)}>← Back</button>
              <button style={styles.nextBtn} onClick={() => setStep(3)} disabled={selectedTechs.length === 0}>
                Continue → ({selectedTechs.length} selected)
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Review */}
        {step === 3 && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>Review & Create</h2>

            <div style={styles.reviewBlock}>
              <div style={styles.reviewRow}><span style={styles.reviewKey}>Project</span><span>{projectTitle}</span></div>
              {clientName && <div style={styles.reviewRow}><span style={styles.reviewKey}>Client</span><span>{clientName}</span></div>}
              {selectedTicket && <div style={styles.reviewRow}><span style={styles.reviewKey}>ME Ticket</span><span>#{selectedTicket.id}</span></div>}
              <div style={styles.reviewRow}>
                <span style={styles.reviewKey}>Technologies</span>
                <span>{selectedTechs.map(t => t.technology.replace('_', ' ')).join(', ')}</span>
              </div>
            </div>

            <p style={styles.stepHint}>After creating, you'll upload files per technology and generate the report.</p>

            <div style={styles.navRow}>
              <button style={styles.backStepBtn} onClick={() => setStep(2)}>← Back</button>
              <button style={styles.nextBtn} onClick={createReport} disabled={saving}>
                {saving ? 'Creating…' : 'Create Report →'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f4f6f9', fontFamily: 'system-ui, sans-serif' },
  header: {
    background: '#0E2841', color: '#fff', padding: '0 32px',
    height: 60, display: 'flex', alignItems: 'center', gap: 16,
  },
  backBtn: { background: 'transparent', border: 'none', color: '#cdd9e5', fontSize: 14, cursor: 'pointer' },
  headerTitle: { fontSize: 16, fontWeight: 600 },
  stepper: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    padding: '24px 32px', background: '#fff', borderBottom: '1px solid #E8E8E8',
    gap: 0,
  },
  stepItem: { display: 'flex', alignItems: 'center' },
  stepDot: {
    width: 28, height: 28, borderRadius: '50%', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700,
  },
  stepLabel: { fontSize: 12, fontWeight: 600, margin: '0 8px' },
  stepLine: { width: 40, height: 2, margin: '0 4px' },
  main: { maxWidth: 700, margin: '0 auto', padding: '32px 24px' },
  stepContent: {},
  stepTitle: { fontSize: 22, color: '#0E2841', fontWeight: 700, marginBottom: 8 },
  stepHint: { fontSize: 14, color: '#777', marginBottom: 24 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#0E2841', margin: '16px 0 6px' },
  input: {
    width: '100%', padding: '10px 12px', border: '1.5px solid #E8E8E8',
    borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none',
  },
  textarea: {
    width: '100%', padding: '10px 12px', border: '1.5px solid #E8E8E8',
    borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none',
    resize: 'vertical',
  },
  searchRow: { display: 'flex', gap: 8, marginBottom: 16 },
  searchBtn: {
    background: '#156082', color: '#fff', border: 'none',
    padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
  },
  ticketList: { border: '1.5px solid #E8E8E8', borderRadius: 8, overflow: 'hidden', marginBottom: 16 },
  ticketRow: { padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' },
  ticketId: { fontSize: 12, color: '#156082', fontWeight: 600 },
  ticketSubject: { fontSize: 14, color: '#0E2841', fontWeight: 500, margin: '2px 0' },
  ticketMeta: { fontSize: 12, color: '#aaa' },
  selectedTicket: {
    background: '#e8f3f8', border: '1.5px solid #156082', borderRadius: 8,
    padding: '10px 14px', fontSize: 14, color: '#0E2841', marginBottom: 16,
  },
  catBlock: { marginBottom: 24 },
  catLabel: { fontSize: 14, fontWeight: 700, color: '#156082', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 },
  techGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 },
  techCard: {
    padding: '12px 14px', border: '2px solid #E8E8E8', borderRadius: 8,
    cursor: 'pointer', background: '#fff', transition: 'all 0.15s',
  },
  techCardSelected: { border: '2px solid #156082', background: '#e8f3f8' },
  techCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 },
  techName: { fontSize: 14, fontWeight: 600, color: '#0E2841' },
  readyBadge: { fontSize: 10, color: '#196B24', fontWeight: 700, whiteSpace: 'nowrap' },
  soonBadge: { fontSize: 10, color: '#aaa', fontWeight: 600, whiteSpace: 'nowrap' },
  techHints: { fontSize: 11, color: '#aaa', marginTop: 4 },
  reviewBlock: { background: '#fff', borderRadius: 10, padding: 20, marginBottom: 24, border: '1px solid #E8E8E8' },
  reviewRow: { display: 'flex', gap: 16, padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 14 },
  reviewKey: { fontWeight: 600, color: '#0E2841', minWidth: 120 },
  navRow: { display: 'flex', justifyContent: 'space-between', marginTop: 32 },
  nextBtn: {
    background: '#156082', color: '#fff', border: 'none',
    padding: '11px 24px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  backStepBtn: {
    background: '#fff', color: '#0E2841', border: '1.5px solid #E8E8E8',
    padding: '11px 24px', borderRadius: 8, fontSize: 14, cursor: 'pointer',
  },
  skipBtn: {
    background: 'transparent', color: '#aaa', border: 'none',
    padding: '11px 24px', fontSize: 14, cursor: 'pointer',
  },
};
